"use client";

import { CmsText } from "@/components/CmsText";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { RevealGroup, RevealItem } from "@/components/Motion";

export type Leader = {
  name: string;
  role: string;
  bio?: string;
  photo?: string;
  linkedin?: string;
};

const EASE = [0.16, 1, 0.3, 1] as const;

function initialsOf(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
}

function Portrait({ leader, className, cmsKey, c }: { leader: Leader; className: string; cmsKey?: string; c?: Record<string, string> }) {
  const src = (cmsKey && c?.[cmsKey]?.trim()) || leader.photo;
  return src ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={(cmsKey && c?.[`${cmsKey}.alt`]?.trim()) || `${leader.name}, ${leader.role} at Table Space`}
      data-cms-img={cmsKey}
      loading="lazy"
      decoding="async"
      className={className}
    />
  ) : (
    <div className={`flex items-center justify-center bg-brand-surface ${className}`}>
      <span className="t-title text-brand-muted">{initialsOf(leader.name)}</span>
    </div>
  );
}

// Leadership grid with a click-to-open profile modal (large portrait + full
// bio). Hover keeps a subtle zoom + "View profile" hint; the modal carries
// the complete text the old clipped hover overlay couldn't.
export function LeaderGrid({
  leaders,
  variant = "csuite",
  c = {},
  cmsPrefix,
}: {
  leaders: Leader[];
  variant?: "csuite" | "bench";
  c?: Record<string, string>;
  /** CMS key prefix (e.g. "leaders") — enables in-place editing of name/role. */
  cmsPrefix?: string;
}) {
  // The index travels with the selection so the modal can address the same CMS
  // keys as the card behind it — editing a name in either place edits one value.
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const active = activeIdx === null ? null : leaders[activeIdx] ?? null;

  // ESC to close + body scroll lock while open
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setActiveIdx(null);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active]);

  const isCsuite = variant === "csuite";

  return (
    <>
      <RevealGroup
        className={
          isCsuite
            ? "mt-14 grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 lg:grid-cols-4"
            : "mt-14 grid grid-cols-2 gap-x-4 gap-y-10 sm:grid-cols-3 lg:grid-cols-5"
        }
        stagger={isCsuite ? 0.06 : 0.04}
      >
        {leaders.map((l, i) => (
          <RevealItem key={l.name}>
            <button
              type="button"
              onClick={() => setActiveIdx(i)}
              aria-haspopup="dialog"
              className={`group relative w-full overflow-hidden rounded-2xl ring-1 ring-brand-line outline-hidden transition-shadow focus-visible:ring-2 focus-visible:ring-brand-accent ${
                isCsuite ? "aspect-[4/5]" : "aspect-square"
              }`}
            >
              <Portrait
                leader={l}
                c={c}
                cmsKey={cmsPrefix ? `${cmsPrefix}.${i}.photo` : undefined}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              {/* hover hint */}
              <span className="pointer-events-none absolute inset-x-0 bottom-0 flex translate-y-2 items-center justify-between bg-gradient-to-t from-[#1a1a1a]/85 to-transparent p-4 pt-10 opacity-0 transition-all duration-400 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100">
                <span className="text-xs font-medium text-white">View profile</span>
                <span className="text-brand-accent">→</span>
              </span>
            </button>
            <h3
              className={`mt-3 font-medium text-brand-ink ${isCsuite ? "text-base" : "text-sm"}`}
            >
              {cmsPrefix ? <CmsText c={c} k={`${cmsPrefix}.${i}.name`}>{l.name}</CmsText> : l.name}
            </h3>
            <p
              className={`mono-meta mt-1 ${isCsuite ? "text-brand-accent-dark" : "text-brand-muted"}`}
            >
              {cmsPrefix ? <CmsText c={c} k={`${cmsPrefix}.${i}.role`}>{l.role}</CmsText> : l.role}
            </p>
          </RevealItem>
        ))}
      </RevealGroup>

      {/* profile modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#1a1a1a]/70 p-4 backdrop-blur-sm md:p-8"
            onClick={() => setActiveIdx(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`${active.name} — profile`}
          >
            <motion.div
              key="panel"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.985 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              // The row template is what makes the long profiles behave, and
              // it differs by breakpoint because the panel does.
              //
              // From md up the portrait and the bio are two COLUMNS in one
              // row. max-h alone caps the BOX, not the row: a grid row is
              // sized from its content, so an 880-character bio grew the row
              // to 969px inside an 880px panel. overflow-hidden then clipped
              // the bottom of the portrait, and the text column never
              // scrolled because it had already been given all the height it
              // asked for. minmax(0, …) lets the row shrink to the panel, so
              // the portrait fills its column and the bio scrolls in its own.
              //
              // Below md there are no columns, so the same two children stack
              // into two ROWS — and a one-row template leaves the second one
              // implicit and auto-sized. The bio then took the whole panel,
              // the declared row collapsed to 0, and the portrait's h-56
              // overflowed it and covered the role, the name and the opening
              // lines. Naming both rows fixes that: the portrait keeps its
              // fixed band and the bio gets the remainder, shrinkable, so it
              // scrolls inside the panel like it does on the wide layout.
              className="relative grid max-h-[88svh] w-full max-w-3xl grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-2xl bg-brand-paper ring-1 ring-brand-line md:grid-cols-[0.85fr_1.15fr] md:grid-rows-[minmax(0,1fr)]"
            >
              {/* large portrait */}
              <div className="relative h-56 md:h-auto">
                <Portrait
                  leader={active}
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
              </div>
              {/* full bio */}
              <div className="flex min-h-0 flex-col overflow-y-auto p-7 md:p-9">
                {/* Same keys as the card behind, so a name or role edited in
                    either place is the one value. The bio only exists here, so
                    it gets its own key — it was previously not editable at all. */}
                {cmsPrefix ? (
                  <CmsText c={c} k={`${cmsPrefix}.${activeIdx}.role`} as="p" className="eyebrow text-brand-accent-dark">
                    {active.role}
                  </CmsText>
                ) : (
                  <p className="eyebrow text-brand-accent-dark">{active.role}</p>
                )}
                {cmsPrefix ? (
                  <CmsText c={c} k={`${cmsPrefix}.${activeIdx}.name`} as="h3" className="mt-3 t-title text-brand-ink">
                    {active.name}
                  </CmsText>
                ) : (
                  <h3 className="mt-3 t-title text-brand-ink">{active.name}</h3>
                )}
                {cmsPrefix ? (
                  <CmsText c={c} k={`${cmsPrefix}.${activeIdx}.bio`} as="p" className="mt-5 text-base font-normal leading-relaxed text-brand-slate">
                    {active.bio || "Profile coming soon."}
                  </CmsText>
                ) : active.bio ? (
                  <p className="mt-5 text-base font-normal leading-relaxed text-brand-slate">
                    {active.bio}
                  </p>
                ) : (
                  <p className="mt-5 text-sm font-normal text-brand-muted">
                    Profile coming soon.
                  </p>
                )}
                {/* Only when the person has one — an empty row of chrome on
                    every other profile is worse than no link at all.

                    Shaped like the site's own social links (see
                    components/sections/SocialFeed): the mark in a brand-ink
                    tile, the label in brand-accent-dark, and an arrow that
                    steps right on hover, with `group` driving both. It was a
                    LinkedIn-blue pill, which is LinkedIn's colour rather than
                    ours and appeared nowhere else on the site. */}
                {active.linkedin && (
                  <a
                    href={active.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow ${active.name} on LinkedIn`}
                    className="group mt-6 inline-flex w-fit items-center gap-2.5 text-sm font-medium text-brand-accent-dark"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-ink transition-transform duration-300 group-hover:scale-105">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src="/images/social/icons/linkedin-white.svg" alt="" className="h-4 w-4" />
                    </span>
                    <span className="group-hover:underline group-hover:decoration-brand-line group-hover:underline-offset-4">
                      Follow
                    </span>
                    <span aria-hidden className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  </a>
                )}
                <span className="mono-meta mt-8 text-brand-muted">TABLE SPACE LEADERSHIP</span>
              </div>
              {/* close */}
              <button
                type="button"
                onClick={() => setActiveIdx(null)}
                aria-label="Close profile"
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-brand-paper/90 text-brand-ink ring-1 ring-brand-line transition-colors hover:bg-brand-ink hover:text-brand-paper"
              >
                ×
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
